import { useState } from "react";
import { Reveal } from "@/components/Reveal";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Smartphone, BellRing, BadgePercent, Rocket, Apple, Play } from "lucide-react";

const PERKS = [
  { icon: BellRing, text: "Notifiche in tempo reale sul volo del drone sopra casa tua" },
  { icon: BadgePercent, text: "Sconto esclusivo sul primo lavaggio per chi è in lista" },
  { icon: Rocket, text: "Accesso prioritario al lancio dell'app" },
];

export function AppWaitlist() {
  const [email, setEmail] = useState("");
  const [sending, setSending] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      const res = await api.post("/waitlist", { email });
      toast.success(res.data.message);
      setEmail("");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setSending(false);
    }
  };

  return (
    <section id="app" className="py-24 sm:py-32 relative overflow-hidden" data-testid="app-waitlist-section">
      <div
        className="absolute top-0 left-1/2 -translate-x-1/2 w-[700px] h-[400px] rounded-full pointer-events-none"
        style={{ background: "radial-gradient(ellipse, rgba(255,107,0,0.1) 0%, transparent 65%)" }}
      />
      <div className="max-w-7xl mx-auto px-5 sm:px-8 grid lg:grid-cols-2 gap-14 items-center relative z-10">
        <div>
          <Reveal>
            <p className="font-mono-adj text-xs uppercase tracking-[0.3em] text-orange-500 font-semibold">App Mobile in Arrivo</p>
          </Reveal>
          <Reveal delay={0.08}>
            <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight text-white mt-4 leading-[1.05]">
              Il tuo impianto, <span className="fire-text">in tasca</span>
            </h2>
          </Reveal>
          <Reveal delay={0.16}>
            <p className="text-base sm:text-lg text-slate-400 leading-relaxed mt-6">
              L'app AirDroneJet per iOS e Android è in fase di completamento: prenota, segui il
              drone in diretta e gestisci le tue manutenzioni da un unico posto. Iscriviti alla
              waitlist per l'accesso prioritario.
            </p>
          </Reveal>

          <div className="flex flex-col gap-4 mt-8">
            {PERKS.map((p, i) => (
              <Reveal key={p.text} delay={0.2 + i * 0.07}>
                <div className="flex items-center gap-4" data-testid={`waitlist-perk-${i}`}>
                  <div className="w-10 h-10 rounded-xl fire-bg flex items-center justify-center shrink-0">
                    <p.icon size={17} className="text-white" />
                  </div>
                  <p className="text-sm text-slate-300">{p.text}</p>
                </div>
              </Reveal>
            ))}
          </div>

          <Reveal delay={0.4}>
            <form onSubmit={submit} className="flex flex-col sm:flex-row gap-3 mt-10" data-testid="waitlist-form">
              <input
                type="email"
                required
                placeholder="La tua email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="flex-1 bg-[#13141B] border border-white/10 rounded-full px-6 py-4 text-sm text-white placeholder:text-slate-600 focus:outline-none focus:border-orange-500/70 transition-colors duration-300"
                data-testid="waitlist-email-input"
              />
              <button
                type="submit"
                disabled={sending}
                data-testid="waitlist-submit-button"
                className="fire-bg text-white font-mono-adj text-xs uppercase tracking-[0.2em] font-semibold rounded-full px-8 py-4 hover:scale-[1.03] active:scale-95 transition-transform duration-300 disabled:opacity-50"
              >
                {sending ? "Iscrizione..." : "Iscriviti alla Waitlist"}
              </button>
            </form>
          </Reveal>
        </div>

        <Reveal delay={0.2}>
          <div className="relative mx-auto w-[280px] sm:w-[320px]" data-testid="app-phone-mockup">
            <div className="rounded-[3rem] border border-white/15 bg-[#13141B] p-3 shadow-[0_0_80px_rgba(255,107,0,0.15)]">
              <div className="rounded-[2.4rem] bg-[#050507] border border-white/5 overflow-hidden">
                <div className="flex justify-center pt-3">
                  <div className="w-24 h-6 bg-[#13141B] rounded-full" />
                </div>
                <div className="p-6 pt-8 flex flex-col gap-4 min-h-[480px]">
                  <div className="flex items-center gap-2">
                    <Smartphone size={16} className="text-orange-500" />
                    <p className="font-mono-adj text-[10px] uppercase tracking-[0.25em] text-slate-500">AirDroneJet App</p>
                  </div>
                  <p className="font-display font-bold text-3xl text-white leading-tight">
                    Prossimo <span className="fire-text">lavaggio</span>
                  </p>
                  <div className="bg-[#13141B] border border-orange-500/30 rounded-2xl p-4">
                    <p className="font-mono-adj text-[9px] uppercase tracking-[0.2em] text-orange-400">Impianto 6 kW</p>
                    <p className="font-heading font-bold text-white text-lg mt-1">Drone in arrivo</p>
                    <div className="h-1.5 rounded-full bg-white/10 mt-3 overflow-hidden">
                      <div className="h-full w-2/3 fire-bg rounded-full" />
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div className="bg-[#13141B] border border-white/10 rounded-2xl p-4">
                      <p className="font-display font-bold text-2xl fire-text">+35%</p>
                      <p className="font-mono-adj text-[8px] uppercase tracking-[0.15em] text-slate-500 mt-1">Resa stimata</p>
                    </div>
                    <div className="bg-[#13141B] border border-white/10 rounded-2xl p-4">
                      <p className="font-display font-bold text-2xl text-white">€400</p>
                      <p className="font-mono-adj text-[8px] uppercase tracking-[0.15em] text-slate-500 mt-1">Online o da accordare</p>
                    </div>
                  </div>
                  <div className="mt-auto flex gap-3">
                    <div className="flex-1 border border-white/15 rounded-full py-3 flex items-center justify-center gap-2">
                      <Apple size={14} className="text-slate-300" />
                      <span className="font-mono-adj text-[9px] uppercase tracking-[0.15em] text-slate-400">iOS</span>
                    </div>
                    <div className="flex-1 border border-white/15 rounded-full py-3 flex items-center justify-center gap-2">
                      <Play size={14} className="text-slate-300" />
                      <span className="font-mono-adj text-[9px] uppercase tracking-[0.15em] text-slate-400">Android</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </Reveal>
      </div>
    </section>
  );
}
